import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Activity, CheckCircle2, Clock, ImageIcon, XCircle } from 'lucide-react'

interface StoryLog {
  id: string
  fb_page_name: string
  status: string
  image_filename: string | null
  error_message: string | null
  created_at: number
}

interface StatusPanelProps {
  profileCount: number
  pageCount: number
  mediaCount: number
  connectionCount: number
  activeCount: number
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000)
  if (diff < 60) return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}d`
}

export function StatusPanel({
  profileCount,
  pageCount,
  mediaCount,
  connectionCount,
  activeCount,
}: StatusPanelProps) {
  const { data: logsData } = useQuery<{ logs: StoryLog[] }>({
    queryKey: ['automacao-fb', 'story-logs'],
    queryFn: () => api.get('/automacao-fb/story-logs'),
    refetchInterval: 15000,
  })

  const { data: statusData } = useQuery<{ total_stories: number; success_stories: number }>({
    queryKey: ['automacao-fb', 'status-stats'],
    queryFn: () => api.get('/automacao-fb/status'),
    refetchInterval: 15000,
  })

  const logs = logsData?.logs ?? []
  const totalStories = statusData?.total_stories ?? 0
  const successStories = statusData?.success_stories ?? 0
  const successRate = totalStories > 0 ? Math.round((successStories / totalStories) * 100) : 0

  return (
    <div className="absolute bottom-6 left-6 z-20 rounded-xl bg-[#111113]/90 backdrop-blur-md border border-[#27272a] p-4 w-[260px] shadow-2xl">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-indigo-400" />
        <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Status</span>
      </div>

      <div className="space-y-2">
        <StatusRow label="Perfis" value={profileCount} />
        <StatusRow label="Paginas" value={pageCount} />
        <StatusRow label="Midias" value={mediaCount} />
        <StatusRow label="Conexoes" value={connectionCount} />

        <div className="border-t border-[#27272a] my-2" />

        <StatusRow label="Stories postados" value={totalStories} highlight />
        <StatusRow label="Sucesso" value={`${successRate}%`} highlight={successRate >= 90} />
      </div>

      {/* Recent logs */}
      {logs.length > 0 && (
        <>
          <div className="border-t border-[#27272a] mt-3 mb-2" />
          <p className="text-[9px] text-zinc-600 uppercase tracking-wider font-semibold mb-2">Recentes</p>
          <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
            {logs.slice(0, 8).map((log) => (
              <div key={log.id} className="flex items-start gap-2">
                {log.status === 'success' ? (
                  <CheckCircle2 className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-zinc-400 truncate">{log.fb_page_name}</p>
                  {log.error_message && (
                    <p className="text-[9px] text-red-400/70 truncate">{log.error_message}</p>
                  )}
                </div>
                <span className="text-[9px] text-zinc-600 shrink-0">{timeAgo(log.created_at)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function StatusRow({
  label,
  value,
  highlight,
}: {
  label: string
  value: number | string
  highlight?: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-zinc-500">{label}</span>
      <span className={`text-xs font-mono font-semibold ${highlight ? 'text-emerald-400' : 'text-zinc-300'}`}>
        {value}
      </span>
    </div>
  )
}
